import React, { useState, useRef, useCallback } from "react";
import { useMemo, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useQuery } from "../Hooks";
import { useTheme } from "styled-components";
import { Flex } from "reflexbox";
import { PageWrapper } from "../UI/Grid";
import Button from "../UI/Button";
import Lines from "./Lines";
// import Details from "./Details";
import Customer from "./Customer";
import Attachments from "./Attachments";
// import Communications from "./Communications";
import Summary from "./Summary";
import formatter from "../../modules/formatter";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import * as qRs from "../../requests/quoteManagementRequests";
import { createRMAFromQuote } from "../../requests/RMAManagementRequests";
import Loading from "../UI/Loading";
import { getCustomer } from "../../requests/customerManagementRequests";
import HeadingSection from "../UI/HeadingSection";
import AlertContext from "../../context/alert/AlertContext";
import UserContext from "../../context/user/UserContext";
import { Body } from "../UI/Text";

const { getQuote, getCommunications } = qRs;

const Quote = () => {
  const { quoteId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();

  const { setQuote, quote, setPrinting } = useContext(ConfiguratorContext);
  const { showFailureAlert } = useContext(AlertContext);
  const { isLoading: userLoading, activeUser } = useContext(UserContext);

  const [preview, setPreview] = useState(false);

  // Get Communications
  const _comms = useQuery({
    queryID: `comms|${quoteId}`,
    skip: !quoteId || userLoading,
    request: getCommunications,
    queryVariables: { quoteId },
    forcedFilters: {},
  });

  // Get Quote
  const _quote = useQuery({
    queryID: `quote|${quoteId}`,
    skip: !quoteId || userLoading || !_comms.data || _comms.isLoading,
    request: getQuote,
    forcedFilters: {},
    queryVariables: { id: +quoteId },
  });

  const customerQueryVariables = useMemo(() => {
    if (activeUser?.roleLevel === 0) return { quoteId };
    if (_quote.data?.contactId) return { id: _quote.data?.contactId };
    else return { quoteId };
  }, [_quote.data, quoteId, activeUser]);

  const _customer = useQuery({
    queryID: `customer|quote|${quoteId}`,
    request: getCustomer,
    queryVariables: customerQueryVariables,
    skip:
      !quoteId ||
      !_comms.data ||
      _comms.isLoading ||
      !_quote.data ||
      _quote.isLoading ||
      userLoading,
  });

  const { data: customerData, refetchQuery: refetchCustomer } = _customer || {};

  const { customer, contact, billingAddress, shippingAddresses } =
    customerData || {};

  useEffect(() => {
    setQuote({
      ..._quote.data,
      customer,
      contact,
      billingAddress,
      shippingAddresses,
    });
  }, [
    billingAddress,
    contact,
    customer,
    _quote.data,
    setQuote,
    shippingAddresses,
  ]);

  useEffect(() => {
    if (
      !!quote &&
      quote?.id &&
      quote?.sameAsBilling &&
      quote?.billingAddress &&
      !_quote?.isLoading &&
      quote?.shippingAddress?.id !== quote?.billingAddress?.id
    ) {
      setQuote({
        ...quote,
        shippingAddress: quote?.billingAddress,
      });
    }
  }, [quote, setQuote, _quote]);

  const printRef = useRef();

  const [stage, setStage] = useState(
    +window.sessionStorage.getItem(`quoteId:${quoteId}`) || 0
  );
  useEffect(() => {
    if (!quote) return;
    else {
      if (quote.type === 2 && stage !== 0) setStage(0);
    }
  }, [quote, quoteId, stage]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const onPrintClick = useCallback(() => {
    setTimeout(() => {
      handlePrint();
      setPrinting(false);
    }, 0);
  }, [handlePrint, setPrinting]);

  const commonProps = useMemo(
    () => ({
      isLoading:
        _quote?.isLoading ||
        _comms.isLoading ||
        userLoading ||
        !_customer.data ||
        !_comms.data ||
        !_quote.data ||
        _customer.isLoading,
      refetchQuote: _quote?.refetchQuery,
    }),
    [_comms, _customer, _quote, userLoading]
  );

  const title = useMemo(() => {
    let type = "Quote";
    if (quote?.type === 2) type = "Order";
    if (quote?.type === 3) type = "Quote Request";

    let status = "Ordered";
    if (quote?.csiStatus === "W") status = "Submitted";

    return `${type} #${
      quote?.type === 2 ? (quote?.erpOrderNo || "").trim() : quote?.id
    }${quote?.type === 2 ? ` "${status}"` : ""}`;
  }, [quote]);

  const price = useMemo(() => {
    let total = 0;

    if (quote?.lines && quote?.lines?.length > 0) {
      for (let line of quote?.lines) {
        const { quantity = 1, price } = line;
        total += quantity * price;
      }

      return `Total: $${formatter.currency(total)}`;
    }

    return `Total: $${formatter.currency(0)}`;
  }, [quote]);

  const stages = useMemo(() => {
    const stageArray = [];

    if (!!quote && quote?.type !== 2) {
      stageArray.push({
        id: "Lines",
        component: <Lines title="Lines" {...commonProps} />,
      });
      if (activeUser?.roleLevel > 1) {
        stageArray.push({
          id: "Customer",
          component: (
            <Customer refetchCustomer={refetchCustomer} {...commonProps} />
          ),
        });
      }
    }
    stageArray.push({
      id: "Summary",
      component: (
        <Summary
          {...commonProps}
          printRef={printRef}
          preview={preview}
          setPreview={setPreview}
          communications={_comms?.data}
        />
      ),
    });    

    return stageArray;
  }, [quote, commonProps, refetchCustomer, preview, _comms.data, activeUser]);

  const onSetStage = useCallback(
    (_stage) => {
      setStage(_stage);
      window.sessionStorage.setItem(`quoteId:${quoteId}`, _stage);
    },
    [quoteId]
  );

  const onCreateRMA = useCallback(async () => {
    const { id: rmaId } = await createRMAFromQuote(quoteId);
    if (rmaId) return navigate(`/RMA/${rmaId}`);
    return showFailureAlert("Error creating RMA from Order.");
  }, [navigate, quoteId, showFailureAlert]);

  const CreateRMAButton = useMemo(() => {
    return quote?.type === 2 ? (
      <Flex key="create-rma-button">
        <Button
          title="Create RMA"
          iconName="faPersonWalkingArrowLoopLeft"
          onClick={() => onCreateRMA()}
        >
          Create RMA
        </Button>
      </Flex>
    ) : null;
  }, [onCreateRMA, quote]);

  const LeftButton = useMemo(() => {
    return (
      <Button
        size="small"
        key="left"
        color={theme.secondary}
        reverse
        mr="1rem"
        style={{
          opacity: !stage ? 0 : 1,
          cursor: !stage ? "auto" : "pointer",
          pointerEvents: !stage ? "none" : "",
        }}
        iconName="faAngleLeft"
        onClick={() => {
          if (stage - 1 === -1) return null;
          return onSetStage(stage - 1);
        }}
      />
    );
  }, [onSetStage, stage, theme.secondary]);

  const Stages = useMemo(() => {
    return stages.map(({ id }, i) => (
      <Button
        size="small"
        className={stage !== i ? "hide" : undefined}
        color={theme.secondary}
        disabled={!quote?.lines && id === "Summary"}
        reverse={stage !== i}
        key={`${id}_${i}`}
        onClick={() => onSetStage(i)}
      >
        {id}
      </Button>
    ));
  }, [onSetStage, quote, stage, stages, theme.secondary]);

  const RightButton = useMemo(() => {
    return (
      <Button
        size="small"
        key="right"
        color={theme.secondary}
        disabled={stage === stages.length - 1 && !quote?.lines}
        reverse
        ml="1rem"
        style={{
          opacity: stage === stages.length - 1 ? 0 : 1,
          cursor: stage === stages.length - 1 ? "auto" : "pointer",
          pointerEvents: stage === stages.length - 1 ? "none" : "",
        }}
        iconName="faAngleRight"
        onClick={() => {
          if (stage === stages.length - 1) return null;
          return onSetStage(stage + 1);
        }}
      />
    );
  }, [onSetStage, quote, stage, stages, theme.secondary]);

  const Stage = useMemo(
    () => stages?.[stage]?.component || null,
    [stages, stage]
  );

  return _quote?.isLoading ||
    _comms.isLoading ||
    userLoading ||
    !_customer.data ||
    !_comms.data ||
    !_quote.data ||
    _customer.isLoading ? (
    <Loading />
  ) : (
    <>
      <PageWrapper className="no-print">
        <HeadingSection
          my="1rem"
          hideCreate={stages?.[stage]?.id !== "Summary"}
          createLink={() => onPrintClick()}
          createButtonColor={
            stages?.[stage]?.id === "Summary" ? theme.gray : undefined
          }
          createButtonIcon={
            stages?.[stage]?.id === "Summary" ? "faPrint" : "faCartPlus"
          }
          title={title}
          subtitle={price}
          centerSubtitle
          boldSubtitle
          backButtonLink="/"
          centerTitle
          inlineAdditionalElements
          //additionalElements={CreateRMAButton ? [CreateRMAButton] : undefined}
        />      

        {/* {quote?.type === 2 ? (
          <>
            <Body className="no-print" bold center>
              Customer PO:{" "}
              <span style={{ color: theme.secondary }}>{quote?.poNumber}</span>
            </Body>
          </>
        ) : null} */}
        <Flex mb="1rem" width={1} justifyContent="center">
          {LeftButton}
          {Stages}
          {RightButton}
        </Flex>

        {Stage}

        {stage === 0 && (
          <Flex
            flexDirection={["column", "column", "column", "row"]}
            alignItems="flex-start"
            width={1}
            style={{ gap: "1rem" }}
            pt="1rem"
          >
            {/* <Communications
              communications={_comms?.data}
              setData={_comms?.setData}
            /> */}
            {/* <Attachments
              readonly={quote?.type === 2}
              attachmentType={quote?.type === 2 ? 2 : 1}
            /> */}
          </Flex>
        )}
      </PageWrapper>
    </>
  );
};

export default Quote;
